import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  type SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface JsonViewerProps {
  data: any;
  maxInitialDepth?: number;
  showCopyButton?: boolean;
  compact?: boolean;
  sx?: SxProps;
}

interface JsonNodeProps {
  data: any;
  keyName?: string;
  depth: number;
  maxDepth: number;
  isLast?: boolean;
  compact?: boolean;
}

// Helper function to get the type and display info for a value
function getValueInfo(value: any) {
  if (value === null) return { type: 'null', displayValue: 'null', color: '#999' };
  if (value === undefined) return { type: 'undefined', displayValue: 'undefined', color: '#999' };
  if (typeof value === 'string')
    return { type: 'string', displayValue: `"${value}"`, color: '#22c55e' };
  if (typeof value === 'number')
    return { type: 'number', displayValue: value.toString(), color: '#3b82f6' };
  if (typeof value === 'boolean')
    return { type: 'boolean', displayValue: value.toString(), color: '#f59e0b' };
  if (Array.isArray(value))
    return { type: 'array', displayValue: `Array(${value.length})`, color: '#8b5cf6' };
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    return { type: 'object', displayValue: `Object {${keys.length} keys}`, color: '#06b6d4' };
  }
  return { type: 'unknown', displayValue: String(value), color: '#999' };
}

// Helper function to format keys nicely
function formatKey(key: string): string {
  // Convert snake_case to readable format
  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

// Main JSON node component
function JsonNode({
  data,
  keyName,
  depth,
  maxDepth,
  isLast = true,
  compact = false,
}: JsonNodeProps) {
  const [expanded, setExpanded] = useState(depth < maxDepth);
  const valueInfo = getValueInfo(data);
  const isExpandable = valueInfo.type === 'object' || valueInfo.type === 'array';

  const indentLevel = depth * (compact ? 12 : 20);

  const handleCopy = (value: any) => {
    navigator.clipboard.writeText(JSON.stringify(value, null, 2));
  };

  // Render primitive values
  if (!isExpandable) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          ml: indentLevel / 8,
          minHeight: compact ? 24 : 28,
        }}
      >
        {keyName && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 500,
              color: '#64748b',
              mr: 1,
              fontSize: compact ? '0.75rem' : '0.875rem',
            }}
          >
            {formatKey(keyName)}:
          </Typography>
        )}
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontFamily: 'monospace',
            color: valueInfo.color,
            fontSize: compact ? '0.75rem' : '0.875rem',
            wordBreak: 'break-all',
          }}
        >
          {valueInfo.displayValue}
        </Typography>
        {!compact && (
          <Tooltip title="Copy value">
            <IconButton
              size="small"
              onClick={() => handleCopy(data)}
              sx={{ ml: 1, opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Render expandable objects/arrays
  const isArray = Array.isArray(data);
  const items = isArray ? data : Object.entries(data);
  const isEmpty = items.length === 0;

  return (
    <Box sx={{ ml: depth > 0 ? indentLevel / 8 : 0 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: compact ? 32 : 36,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.50' },
          borderRadius: 1,
          px: 0.5,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{ mr: 0.5 }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        {keyName && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mr: 1,
              fontSize: compact ? '0.8rem' : '0.875rem',
            }}
          >
            {formatKey(keyName)}:
          </Typography>
        )}

        <Chip
          label={isArray ? `Array[${items.length}]` : `Object{${items.length}}`}
          size="small"
          variant="outlined"
          sx={{
            height: compact ? 20 : 24,
            fontSize: compact ? '0.7rem' : '0.75rem',
            color: valueInfo.color,
            borderColor: valueInfo.color,
            mr: 1,
          }}
        />

        {!isEmpty && !expanded && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              fontSize: compact ? '0.7rem' : '0.75rem',
            }}
          >
            {isArray
              ? `[${items
                  .slice(0, 3)
                  .map((item: any) => getValueInfo(item).type)
                  .join(', ')}${items.length > 3 ? '...' : ''}]`
              : `{${Object.keys(data).slice(0, 3).join(', ')}${Object.keys(data).length > 3 ? '...' : '}}'}`}
          </Typography>
        )}

        {!compact && (
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="Copy object">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(data);
                }}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ borderLeft: compact ? '1px solid #e2e8f0' : '2px solid #e2e8f0', ml: 1 }}>
          {isEmpty ? (
            <Typography
              variant="caption"
              sx={{
                ml: 2,
                color: 'text.secondary',
                fontStyle: 'italic',
                fontSize: compact ? '0.7rem' : '0.75rem',
              }}
            >
              {isArray ? 'Empty array' : 'Empty object'}
            </Typography>
          ) : (
            items.map((item: any, index: number) => {
              if (isArray) {
                return (
                  <JsonNode
                    key={index}
                    data={item}
                    keyName={`[${index}]`}
                    depth={depth + 1}
                    maxDepth={maxDepth}
                    isLast={index === items.length - 1}
                    compact={compact}
                  />
                );
              } else {
                const [key, value] = item;
                return (
                  <JsonNode
                    key={key}
                    data={value}
                    keyName={key}
                    depth={depth + 1}
                    maxDepth={maxDepth}
                    isLast={index === items.length - 1}
                    compact={compact}
                  />
                );
              }
            })
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export function JsonViewer({
  data,
  maxInitialDepth = 2,
  showCopyButton = true,
  compact = false,
  sx,
}: JsonViewerProps) {
  const [globalExpanded, setGlobalExpanded] = useState(false);

  const dataInfo = getValueInfo(data);

  const handleCopyAll = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  const handleToggleAll = () => {
    setGlobalExpanded(!globalExpanded);
  };

  return (
    <Box
      sx={{
        fontFamily: 'monospace',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header */}
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600 }}
          >
            JSON Data ({dataInfo.displayValue})
          </Typography>
          <Stack
            direction="row"
            spacing={1}
          >
            <Tooltip title={globalExpanded ? 'Collapse all' : 'Expand all'}>
              <IconButton
                size="small"
                onClick={handleToggleAll}
              >
                {globalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
            {showCopyButton && (
              <Tooltip title="Copy all data">
                <IconButton
                  size="small"
                  onClick={handleCopyAll}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          p: compact ? 1 : 2,
          overflow: 'auto',
        }}
      >
        <JsonNode
          data={data}
          depth={0}
          maxDepth={globalExpanded ? 10 : maxInitialDepth}
          compact={compact}
        />
      </Box>
    </Box>
  );
}
